import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';

import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';

import { LocationsState } from '../../../../../store/slices/locationsSlice';
import { VisitorInvitationState } from '../../../../../store/slices/visitorInvitationSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../../store/slices/visitorSearchSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { VisitorType } from '../../../../../types/VisitorTypeTypes';
import { VisitorWorkflowFields } from '../../../../../types/visitorWorkflowTypes';
import { Visitor } from '../../../../../types/visitTypes';

export interface VisitorListModalSubmitData {
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  mobilePhone?: string;
  dob?: string;
  visitorTypeId?: number;
}

interface ModalFormProps {
  open: boolean;
  loading?: boolean;
  isInviting: boolean;
  isCheckrInstalled: boolean;
  signInWorkflowFields?: VisitorWorkflowFields;
  visitor?: Partial<Visitor> | null;
  visitors: Visitor[];
  visitorTypes: VisitorType[];
  locationVisitorTypes?: VisitorType[];
  selectedVisitorTypeId?: number;
  onVisitorTypeChange?: (visitorTypeId: number) => void;
  onOk: (data: VisitorListModalSubmitData) => void;
  onClose: () => void;
}

const VisitorListModal = ({
  open,
  loading,
  isInviting,
  isCheckrInstalled,
  signInWorkflowFields,
  visitor,
  visitors,
  visitorTypes,
  selectedVisitorTypeId,
  locationVisitorTypes,
  onVisitorTypeChange,
  onOk,
  onClose,
}: ModalFormProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as NonNullable<LocationsState['globalLocation']>['id'] | undefined;
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];

  const [form] = OpForm.useForm();

  const isSubmitting = useRef(false);

  // Show a loading state while the modal's dependent data (workflow fields,
  // visitor types, integrations) is still being fetched, or while submitting,
  // so fields only become editable once everything has finished rendering.
  const showLoading = Boolean(loading) || isSubmitting.current;

  const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
    (type) =>
      type.workflow?.SignInWorkflowId && type.workflow.SignInWorkflowId !== -1,
  );

  useEffect(() => {
    if (selectedVisitorTypeId !== undefined) {
      form.setFieldsValue({ visitorTypeId: selectedVisitorTypeId });
    }
  }, [selectedVisitorTypeId, form]);

  const handleSubmit = async () => {
    try {
      isSubmitting.current = true;
      const values = await form.validateFields();
      const trimmedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          typeof value === 'string' ? value.trim() : value,
        ]),
      );

      const submitData: VisitorListModalSubmitData = {
        firstName: String(trimmedValues.firstName ?? ''),
        lastName: String(trimmedValues.lastName ?? ''),
        middleName:
          typeof trimmedValues.middleName === 'string'
            ? trimmedValues.middleName
            : undefined,
        email:
          typeof trimmedValues.email === 'string'
            ? trimmedValues.email
            : undefined,
        mobilePhone:
          typeof trimmedValues.mobilePhone === 'string'
            ? trimmedValues.mobilePhone
            : undefined,
        dob:
          typeof trimmedValues.dob === 'string' ? trimmedValues.dob : undefined,
        visitorTypeId:
          typeof trimmedValues.visitorTypeId === 'number'
            ? trimmedValues.visitorTypeId
            : undefined,
      };

      onOk(submitData);
      dispatch(searchVisitorsToday({ orgId }));

      if (Object.keys(lastSearchParams).length !== 0) {
        dispatch(
          searchVisitors({
            ...lastSearchParams,
            orgId,
            locationId: lastSearchParams.locationId || globalLocationId!,
          }),
        );
      }

      form.resetFields();
      onClose();
    } catch {
      notificationService.show('error', t('notification.validationError'));
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <OpModal
      open={open}
      title={
        visitor ? t('visitors.visitorList.edit') : t('visitors.visitorList.add')
      }
      okText={t('visitors.visitorList.save')}
      cancelText={t('visitors.visitorList.cancel')}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleSubmit}
      centered
      width={720}
      closable={!isSubmitting.current}
      maskClosable={!isSubmitting.current}
      footer={showLoading ? null : undefined}
      data-testid="add-visitor-save-button"
    >
      {showLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <OpSpin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
          <div
            style={{
              marginTop: 10,
              fontSize: 14,
              color: 'var(--colorInfoText)',
            }}
          >
            Loading
          </div>
        </div>
      ) : (
        <OpForm
          form={form}
          onSubmit={() => {}}
          hasError={false}
          defaultButtons={false}
          initialValues={
            visitor
              ? { ...visitor, mobilePhone: visitor.mobilePhone || '' }
              : undefined
          }
        >
          <OpForm.Input
            label={t('visitors.visitorList.firstName')}
            name="firstName"
            rules={[
              {
                required: true,
                message: t('visitors.visitorList.firstNameRequired'),
              },
            ]}
          />
          <OpForm.Input
            label={t('visitors.visitorList.lastName')}
            name="lastName"
            rules={[
              {
                required: true,
                message: t('visitors.visitorList.lastNameRequired'),
              },
            ]}
          />

          {!isInviting && signInWorkflowFields?.middleName.included === 1 && (
            <OpForm.Input
              label={t('visitors.visitorList.middleName')}
              name="middleName"
              rules={[
                {
                  required:
                    !isInviting &&
                    signInWorkflowFields?.middleName.required === 1,
                  message: t('visitors.visitorList.middleNameRequired'),
                },
              ]}
            />
          )}

          {visitors.length === 0 && hasVisitorTypeSignInWorkflow && (
            <OpForm.Select
              label={t('signInManuallyDrawer.visitorType.label')}
              name="visitorTypeId"
              rules={[
                {
                  required: true,
                  message: t('signInManuallyDrawer.visitorType.required'),
                },
              ]}
              placeholder={t('signInManuallyDrawer.visitorType.placeholder')}
              options={visitorTypes.map((type) => ({
                label: t(type.languageCode ?? ''),
                value: type.id,
              }))}
              onChange={(value) => {
                form.setFieldsValue({ visitorTypeId: value });
                onVisitorTypeChange?.(Number(value));
              }}
            />
          )}

          {((isInviting && invitationConfig?.email.included === 1) ||
            (!isInviting && signInWorkflowFields?.email.included === 1)) && (
            <OpForm.Input
              label={t('visitors.visitorList.email')}
              name="email"
              rules={[
                {
                  required:
                    (isInviting && invitationConfig?.email.required === 1) ||
                    (!isInviting && signInWorkflowFields?.email.required === 1),
                  message: t('visitors.visitorList.emailRequired'),
                },
                {
                  type: 'email',
                  message: t('visitors.visitorList.emailInvalid'),
                },
              ]}
            />
          )}

          {isCheckrInstalled && (
            <OpForm.DatePicker
              label={t('visitors.visitorList.dob')}
              name="dob"
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current && current > dayjs().endOf('day')
              }
            />
          )}

          {((isInviting && invitationConfig?.visitPhone.included === 1) ||
            (!isInviting &&
              signInWorkflowFields?.visitPhone.included === 1)) && (
            <OpForm.PhoneInput
              label={t('visitors.visitorList.mobilePhone')}
              name="mobilePhone"
              rules={[
                {
                  required:
                    (isInviting &&
                      invitationConfig?.visitPhone.required === 1) ||
                    (!isInviting &&
                      signInWorkflowFields?.visitPhone.required === 1),
                  message: t('visitors.visitorList.mobilePhoneRequired'),
                },
              ]}
            />
          )}
        </OpForm>
      )}
    </OpModal>
  );
};

export default VisitorListModal;
